/**
 * SupportApp — root Preact component for the /admin/support HelpScout-style inbox.
 *
 * Mounted into #support-app by support_layout_controller.js.
 *
 * Data loading: the Stimulus controller fetches data from /admin/support.json
 * and re-mounts this component with fresh props on each update.
 * Subsequent user actions (filter, search, load more) call the store methods
 * which re-fetch and re-mount.
 */

import { h, createContext, Fragment } from 'preact'
import { useContext, useState, useEffect } from 'preact/hooks'
import { ConversationList }   from './ConversationList.jsx'
import { ConversationThread } from './ConversationThread.jsx'
import { SenderContextPanel } from './SenderContextPanel.jsx'

export const SupportStoreContext = createContext(null)

export function useSupportStore() {
  return useContext(SupportStoreContext)
}

// ── Root component ─────────────────────────────────────────────────────────────

export function SupportApp({ conversations = [], statusCounts = {}, loading = false, selectedId: initialSelectedId = null, searchQuery = '', mailboxes = [], currentMailboxId = null, onFetch, onFilter, onSearch, onMailboxChange }) {
  // Initialise from URL if available (e.g. /admin/support/conversations/42)
  function idFromUrl() {
    const m = window.location.pathname.match(/\/admin\/support\/conversations\/(\d+)/)
    return m ? parseInt(m[1], 10) : null
  }

  const [selectedId, setSelectedId] = useState(initialSelectedId ?? idFromUrl())
  const [contextOpen, setContextOpen] = useState(false)

  function handleSelect(id) {
    setSelectedId(id)
    setContextOpen(false)
    if (id) {
      history.pushState({ supportConversationId: id }, '', `/admin/support/conversations/${id}`)
    } else {
      history.pushState({ supportConversationId: null }, '', '/admin/support')
    }
  }

  function handleBack() {
    handleSelect(null)
  }

  // Toggle .show-thread on the nearest .support-layout ancestor so the CSS
  // mobile split-view rules (hiding list / showing thread) can fire.
  useEffect(() => {
    const layout = document.querySelector('.support-layout')
    if (!layout) return
    if (selectedId) {
      layout.classList.add('show-thread')
    } else {
      layout.classList.remove('show-thread')
    }
  }, [selectedId])

  // Sync state when the user navigates back/forward
  useEffect(() => {
    function onPopState(e) {
      const id = e.state?.supportConversationId ?? idFromUrl()
      setSelectedId(id)
      setContextOpen(false)
    }
    window.addEventListener('popstate', onPopState)
    return () => window.removeEventListener('popstate', onPopState)
  }, [])

  return (
    <Fragment>
      {/* Left panel — conversation list */}
      <ConversationList
        conversations={conversations}
        statusCounts={statusCounts}
        loading={loading}
        searchQuery={searchQuery}
        selectedId={selectedId}
        mailboxes={mailboxes}
        currentMailboxId={currentMailboxId}
        onSelectConversation={handleSelect}
        onStatusFilter={status => onFilter?.(status)}
        onSearch={query => onSearch?.(query)}
        onMailboxChange={mailboxId => onMailboxChange?.(mailboxId)}
      />

      {/* Right panel — conversation thread or empty state */}
      {selectedId
        ? (
          <ConversationThread
            conversationId={selectedId}
            onBack={handleBack}
            onOpenContext={() => setContextOpen(true)}
            onThreadUpdated={() => onFetch?.()}
          />
        )
        : (
          <div class="support-thread-panel">
            <div class="support-empty">
              <i class="bi bi-envelope-open" />
              <p>Válasszon ki egy beszélgetést a listából</p>
            </div>
          </div>
        )
      }

      {/* Sender context panel — fixed sidebar on desktop, bottom drawer on mobile */}
      <SenderContextPanel
        conversationId={selectedId}
        mobileDrawerOpen={contextOpen}
        onCloseMobileDrawer={() => setContextOpen(false)}
        onSelectThread={handleSelect}
      />
    </Fragment>
  )
}
